import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Container, Stack } from "@mui/material"
import { useAuth } from "@/features/auth/context/AuthContext"
import { useProducts } from "@/shared/hooks/useProducts"
import { useDeleteProduct } from "@/features/listings/hooks/useDeleteProduct"
import { useListingsPagination } from "@/features/listings/hooks/useListingsPagination"
import { LoadingListings, EmptyListings } from "@/features/listings/components/EmptyListings"
import DeleteProductDialog from "@/features/listings/components/DeleteProductDialog"
import ListingsToolbar from "@/features/listings/components/ListingsToolbar"
import ListingsResults from "@/features/listings/components/ListingsResults"
import ListingsPagination from "@/features/listings/components/ListingsPagination"
import { exportProductsToCsv } from "@/shared/utils/csv"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import ImageGallery from "@/shared/components/image-gallery"
import { useImageGalleryViewer } from "@/shared/hooks/useImageGalleryViewer"
import RegenerateImageDialog from "@/shared/components/regenerate-image-dialog"
import { Product } from "@/shared/types/product"
import {
  regenerateProductImage,
  ProductImageRegenerationError,
  updateProductDetails,
  pushProductToAmazon,
  initAmazonOAuth,
  getAmazonConnectionStatus,
  disconnectAmazon,
  AmazonConnectionStatus,
  PushListingRequest
} from "@/shared/services/products"
import UpgradeDialog from "@/features/billing/components/UpgradeDialog"
import { useAiImageQuota } from "@/shared/hooks/useAiImageQuota"
import { ImageGalleryItem } from "@/shared/components/image-gallery"
import { EditableProductFields } from "@/features/listings/components/ProductListingCard"
import AmazonConnectionCard from "@/features/listings/components/AmazonConnectionCard"
import PublishToAmazonDialog from "@/features/listings/components/PublishToAmazonDialog"
import { AMAZON_PUBLISHING_ENABLED } from "@/shared/constants/featureFlags"

const MyListings: React.FC = () => {
  const { user } = useAuth()
  const { products, loading, setProducts } = useProducts(user?.uid ?? null)
  const { deleteDialogOpen, productToDelete, deleting, openDeleteDialog, closeDeleteDialog, handleConfirmDelete } =
    useDeleteProduct()
  const { searchQuery, setSearchQuery, page, setPage, pageCount, filteredProducts, pagedProducts } =
    useListingsPagination(products)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { viewer, openWithItems, closeViewer } = useImageGalleryViewer()
  const { showSnackbar } = useSnackbar()
  const quota = useAiImageQuota(user?.uid ?? null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [regeneratingKeys, setRegeneratingKeys] = useState<Set<string>>(new Set())
  const [differentPromptState, setDifferentPromptState] = useState<{
    product: Product
    imageIndex: number
  } | null>(null)
  const [amazonStatus, setAmazonStatus] = useState<AmazonConnectionStatus>({
    available: false,
    connected: false
  })
  const [amazonStatusLoading, setAmazonStatusLoading] = useState(true)
  const [amazonProduct, setAmazonProduct] = useState<Product | null>(null)
  const [amazonPublishing, setAmazonPublishing] = useState(false)

  useEffect(() => {
    if (!AMAZON_PUBLISHING_ENABLED) {
      setAmazonStatus({ available: false, connected: false })
      setAmazonStatusLoading(false)
      return
    }

    if (!user) {
      setAmazonStatus({ available: false, connected: false })
      setAmazonStatusLoading(false)
      return
    }

    let active = true
    setAmazonStatusLoading(true)
    getAmazonConnectionStatus()
      .then(status => {
        if (active) setAmazonStatus(status)
      })
      .catch(error => {
        console.error("Failed to load Amazon connection status", error)
        if (active) setAmazonStatus({ available: false, connected: false })
      })
      .finally(() => {
        if (active) setAmazonStatusLoading(false)
      })
    return () => {
      active = false
    }
  }, [showSnackbar, user])

  useEffect(() => {
    if (!AMAZON_PUBLISHING_ENABLED) return

    const query = new URLSearchParams(window.location.search)
    const amazonResult = query.get("amazon")
    if (!amazonResult) return

    if (amazonResult === "connected") {
      showSnackbar("Amazon store connected successfully.", "success")
      getAmazonConnectionStatus()
        .then(setAmazonStatus)
        .catch(() => undefined)
    } else if (amazonResult === "error") {
      showSnackbar("Amazon could not be connected. Please try again.", "error")
    }
    query.delete("amazon")
    query.delete("reason")
    const nextSearch = query.toString()
    window.history.replaceState({}, "", `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`)
  }, [showSnackbar])

  const getRegenerationKey = useCallback((productId: string, imageIndex: number) => `${productId}:${imageIndex}`, [])
  const isRegenerating = useCallback(
    (productId: string, imageIndex: number) => regeneratingKeys.has(getRegenerationKey(productId, imageIndex)),
    [getRegenerationKey, regeneratingKeys]
  )

  const regenerateDisabledReason = quota.isLoading
    ? "Checking credits…"
    : quota.isExhausted
      ? "You have no remaining AI image credits."
      : null

  const handleDeleteSuccess = useCallback(
    (deletedId: string) => {
      setProducts(prev => prev.filter(p => p.id !== deletedId))
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(deletedId)
        return next
      })
    },
    [setProducts, setSelectedIds]
  )

  const handleUpdateProduct = useCallback(
    async (productId: string, updates: EditableProductFields) => {
      try {
        const nextProduct = await updateProductDetails({
          productId,
          ...updates
        })
        setProducts(prev => prev.map(product => (product.id === productId ? { ...product, ...nextProduct } : product)))
        showSnackbar("Product updated successfully.", "success")
      } catch (error) {
        showSnackbar("Failed to update product. Please try again.", "error")
        throw error
      }
    },
    [setProducts, showSnackbar]
  )

  const handlePushToAmazon = useCallback(async (product: Product) => {
    setAmazonProduct(product)
  }, [])

  const handleConnectAmazon = useCallback(
    async (marketplaceCode: string) => {
      try {
        const { url } = await initAmazonOAuth(marketplaceCode)
        window.location.assign(url)
      } catch (error) {
        const message =
          error instanceof Error && error.message ? error.message : "Could not start Amazon authorization."
        showSnackbar(message, "error")
      }
    },
    [showSnackbar]
  )

  const handleDisconnectAmazon = useCallback(async () => {
    try {
      await disconnectAmazon()
      setAmazonStatus({ available: true, connected: false })
      showSnackbar("Amazon store disconnected and stored credentials deleted.", "success")
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : "Could not disconnect Amazon."
      showSnackbar(message, "error")
    }
  }, [showSnackbar])

  const handlePublishAmazon = useCallback(
    async (request: PushListingRequest) => {
      setAmazonPublishing(true)
      try {
        const result = await pushProductToAmazon(request)
        showSnackbar(`Amazon accepted SKU ${result.sku} for ${result.marketplaceName}.`, "success")
        setAmazonProduct(null)
      } catch (error) {
        const message = error instanceof Error && error.message ? error.message : "Amazon rejected the listing."
        showSnackbar(message, "error")
      } finally {
        setAmazonPublishing(false)
      }
    },
    [showSnackbar]
  )

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const allFilteredSelected = filteredProducts.every(p => prev.has(p.id))
      const next = new Set(prev)
      if (allFilteredSelected) {
        filteredProducts.forEach(p => next.delete(p.id))
      } else {
        filteredProducts.forEach(p => next.add(p.id))
      }
      return next
    })
  }, [filteredProducts])

  const handleExportSelected = useCallback(() => {
    const selected = products.filter(p => selectedIds.has(p.id))
    exportProductsToCsv(selected)
    setSelectedIds(new Set())
  }, [products, selectedIds])

  const allSelected = useMemo(
    () => filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id)),
    [filteredProducts, selectedIds]
  )
  const someSelected = useMemo(
    () => filteredProducts.some(p => selectedIds.has(p.id)) && !allSelected,
    [filteredProducts, selectedIds, allSelected]
  )

  const runRegeneration = useCallback(
    async (product: Product, imageIndex: number, promptOverride?: { promptId?: string; customPrompt?: string }) => {
      if (!user) return

      if (quota.isExhausted) {
        setUpgradeOpen(true)
        return
      }

      const regenerationKey = getRegenerationKey(product.id, imageIndex)
      setRegeneratingKeys(prev => new Set(prev).add(regenerationKey))

      try {
        await regenerateProductImage({
          productId: product.id,
          generatedImageIndex: imageIndex,
          ...promptOverride
        })
        showSnackbar("Image regenerated successfully.", "success")
      } catch (error) {
        const normalizedError =
          error instanceof ProductImageRegenerationError
            ? error
            : new ProductImageRegenerationError("unknown", "Failed to regenerate image. Please try again.")

        if (normalizedError.code === "resource-exhausted") {
          setUpgradeOpen(true)
          showSnackbar("You've run out of AI image credits.", "warning")
        } else {
          showSnackbar(normalizedError.message, "error")
        }
      } finally {
        setRegeneratingKeys(prev => {
          const next = new Set(prev)
          next.delete(regenerationKey)
          return next
        })
      }
    },
    [getRegenerationKey, quota.isExhausted, showSnackbar, user]
  )

  const handleRegenerateSame = useCallback(
    (product: Product, imageIndex: number) => {
      void runRegeneration(product, imageIndex)
    },
    [runRegeneration]
  )

  const handleOpenDifferentPrompt = useCallback((product: Product, imageIndex: number) => {
    setDifferentPromptState({ product, imageIndex })
  }, [])

  const handleSubmitDifferentPrompt = useCallback(
    async ({ promptId, customPrompt }: { promptId?: string; customPrompt?: string }) => {
      if (!differentPromptState) return

      await runRegeneration(differentPromptState.product, differentPromptState.imageIndex, {
        promptId,
        customPrompt
      })
      setDifferentPromptState(null)
    },
    [differentPromptState, runRegeneration]
  )

  const getGalleryRegenerateDisabledReason = useCallback(
    (item: ImageGalleryItem) => {
      const metadata = item.metadata ?? {}
      if (typeof metadata.generatedImageIndex !== "number" || typeof metadata.productId !== "string") {
        return "Regeneration is only available for generated images."
      }
      return regenerateDisabledReason
    },
    [regenerateDisabledReason]
  )

  const handleGalleryRegenerateSame = useCallback(
    (item: ImageGalleryItem) => {
      const metadata = item.metadata ?? {}
      if (typeof metadata.productId !== "string" || typeof metadata.generatedImageIndex !== "number") return
      const product = products.find(entry => entry.id === metadata.productId)
      if (!product) return
      handleRegenerateSame(product, metadata.generatedImageIndex)
    },
    [handleRegenerateSame, products]
  )

  const handleGalleryRegenerateDifferent = useCallback(
    (item: ImageGalleryItem) => {
      const metadata = item.metadata ?? {}
      if (typeof metadata.productId !== "string" || typeof metadata.generatedImageIndex !== "number") return
      const product = products.find(entry => entry.id === metadata.productId)
      if (!product) return
      handleOpenDifferentPrompt(product, metadata.generatedImageIndex)
    },
    [handleOpenDifferentPrompt, products]
  )

  const isGalleryItemRegenerating = useCallback(
    (item: ImageGalleryItem) => {
      const metadata = item.metadata ?? {}
      if (typeof metadata.productId !== "string" || typeof metadata.generatedImageIndex !== "number") return false
      return isRegenerating(metadata.productId, metadata.generatedImageIndex)
    },
    [isRegenerating]
  )

  if (loading) return <LoadingListings />
  if (products.length === 0) {
    return (
      <Container maxWidth={false} sx={{ pb: 10, padding: { xs: 2, sm: 3, md: 4 } }}>
        {AMAZON_PUBLISHING_ENABLED && amazonStatus.available && (
          <AmazonConnectionCard
            loading={amazonStatusLoading}
            status={amazonStatus}
            onConnect={handleConnectAmazon}
            onDisconnect={handleDisconnectAmazon}
          />
        )}
        <EmptyListings />
      </Container>
    )
  }

  return (
    <>
      <Container maxWidth={false} sx={{ pb: 10, padding: { xs: 2, sm: 3, md: 4 } }}>
        <Stack gap={1} pb={10}>
          {AMAZON_PUBLISHING_ENABLED && amazonStatus.available && (
            <AmazonConnectionCard
              loading={amazonStatusLoading}
              status={amazonStatus}
              onConnect={handleConnectAmazon}
              onDisconnect={handleDisconnectAmazon}
            />
          )}
          <ListingsToolbar
            selectedCount={selectedIds.size}
            allSelected={allSelected}
            someSelected={someSelected}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onToggleSelectAll={toggleSelectAll}
            onExportSelected={handleExportSelected}
          />
          <ListingsResults
            filteredProducts={filteredProducts}
            pagedProducts={pagedProducts}
            selectedIds={selectedIds}
            onView={(items: ImageGalleryItem[], initialIndex: number) => openWithItems(items, initialIndex)}
            onDelete={openDeleteDialog}
            onToggleSelect={toggleSelect}
            onRegenerateSame={handleRegenerateSame}
            onRegenerateDifferent={handleOpenDifferentPrompt}
            isRegenerating={isRegenerating}
            regenerateDisabledReason={regenerateDisabledReason}
            onUpdate={handleUpdateProduct}
            onPushToAmazon={handlePushToAmazon}
            amazonPublishingAvailable={AMAZON_PUBLISHING_ENABLED && amazonStatus.available === true}
            canPublishToAmazon={AMAZON_PUBLISHING_ENABLED && amazonStatus.available === true && amazonStatus.connected}
          />
        </Stack>
        <DeleteProductDialog
          open={deleteDialogOpen}
          product={productToDelete}
          deleting={deleting}
          onClose={closeDeleteDialog}
          onConfirm={() => {
            if (!user) return
            handleConfirmDelete(user.uid, handleDeleteSuccess).catch(() => {
              showSnackbar("Failed to delete product. Please try again.", "error")
            })
          }}
        />
        <ListingsPagination pageCount={pageCount} page={page} onPageChange={setPage} />
      </Container>
      {viewer && (
        <ImageGallery
          items={viewer.items}
          initialIndex={viewer.initialIndex}
          onClose={closeViewer}
          regenerationHandlers={{
            onRegenerateSame: handleGalleryRegenerateSame,
            onRegenerateDifferent: handleGalleryRegenerateDifferent,
            isRegenerating: isGalleryItemRegenerating,
            getRegenerateDisabledReason: getGalleryRegenerateDisabledReason
          }}
        />
      )}
      <RegenerateImageDialog
        open={Boolean(differentPromptState)}
        onClose={() => setDifferentPromptState(null)}
        onSubmit={handleSubmitDifferentPrompt}
        loading={
          differentPromptState
            ? isRegenerating(differentPromptState.product.id, differentPromptState.imageIndex)
            : false
        }
        currentPromptText={
          differentPromptState?.product.generatedImages?.[differentPromptState.imageIndex]?.promptText ?? undefined
        }
      />
      <UpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason={`You have ${quota.remaining} AI image credit${quota.remaining !== 1 ? "s" : ""} remaining.`}
      />
      {AMAZON_PUBLISHING_ENABLED && (
        <PublishToAmazonDialog
          product={amazonProduct}
          loading={amazonPublishing}
          onClose={() => setAmazonProduct(null)}
          onSubmit={handlePublishAmazon}
        />
      )}
    </>
  )
}

export default MyListings
